const SECRET_KEY = "TRS-App-Secure-Storage-Salt-2026";

const uint8ArrayToBinaryString = (arr: Uint8Array): string => {
  let result = "";
  for (let i = 0; i < arr.length; i++) {
    result += String.fromCharCode(arr[i]);
  }
  return result;
};

/**
 * Encrypts a string using a symmetric XOR cipher and includes an integrity checksum.
 * @param text The plain text to encrypt.
 * @returns Base64 encoded string containing the encrypted payload and checksum, or an empty string on error.
 */
export const encrypt = (text: string): string => {
  try {
    const textBytes = new TextEncoder().encode(text);
    const keyBytes = new TextEncoder().encode(SECRET_KEY);
    const encryptedBytes = new Uint8Array(textBytes.length);
    for (let i = 0; i < textBytes.length; i++) {
      encryptedBytes[i] = textBytes[i] ^ keyBytes[i % keyBytes.length];
    }
    
    // Calculate checksum of the original text
    let checksum = 0;
    for (let i = 0; i < text.length; i++) {
      checksum = (checksum + text.charCodeAt(i)) % 65536;
    }
    
    // Package ciphertext and checksum
    const binaryStr = uint8ArrayToBinaryString(encryptedBytes);
    const payload = {
      c: btoa(binaryStr),
      s: checksum
    };
    
    return btoa(JSON.stringify(payload));
  } catch (e) {
    console.error("SecureStorage encryption error:", e);
    return "";
  }
};

/**
 * Decrypts a string encrypted with the encrypt function and verifies its integrity checksum.
 * @param encryptedData The base64 encoded payload.
 * @returns The decrypted plain text, or null if decryption/checksum verification fails.
 */
export const decrypt = (encryptedData: string): string | null => {
  try {
    if (!encryptedData) return null;
    const rawPayload = atob(encryptedData);
    const payload = JSON.parse(rawPayload);
    if (!payload || typeof payload.c !== 'string' || typeof payload.s !== 'number') {
      return null;
    }
    
    const binary = atob(payload.c);
    const textBytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      textBytes[i] = binary.charCodeAt(i);
    }
    
    const keyBytes = new TextEncoder().encode(SECRET_KEY);
    const decryptedBytes = new Uint8Array(textBytes.length);
    for (let i = 0; i < textBytes.length; i++) {
      decryptedBytes[i] = textBytes[i] ^ keyBytes[i % keyBytes.length];
    }
    
    const decryptedText = new TextDecoder().decode(decryptedBytes);
    
    // Verify checksum
    let checksum = 0;
    for (let i = 0; i < decryptedText.length; i++) {
      checksum = (checksum + decryptedText.charCodeAt(i)) % 65536;
    }
    
    if (checksum !== payload.s) {
      console.warn("SecureStorage: Data integrity checksum failed. Possible tampering detected.");
      return null;
    }
    
    return decryptedText;
  } catch (e) {
    console.error("SecureStorage decryption error:", e);
    return null;
  }
};
